import { Check, Loader2 } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { checkPromoCode } from '../../api/billingApi'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'

const TONES = {
  dark: {
    link: 'text-white/70 hover:text-white',
    input: 'border-white/20 bg-white/[0.06] text-white placeholder:text-white/35 focus:border-[#3B82F6]',
    button: 'border-white/25 bg-white/[0.08] text-white hover:bg-white/[0.14]',
    applied: 'border-[#22C55E]/40 bg-[#22C55E]/10 text-white',
    muted: 'text-white/60',
    error: 'text-[#FCA5A5]'
  },
  light: {
    link: 'text-[#1F6FEB] hover:underline',
    input: 'border-[#D1D5DB] bg-white text-[#0A1628] placeholder:text-[#9CA3AF] focus:border-[#2563EB]',
    button: 'border-[#D1D5DB] bg-white text-[#0A1628] hover:bg-[#F3F4F6]',
    applied: 'border-[#BBF7D0] bg-[#F0FDF4] text-[#0A1628]',
    muted: 'text-[#6B7280]',
    error: 'text-[#B42318]'
  }
}

/**
 * "Have a promo code?" for a new location's first plan: the code is checked with the backend (it is only for new
 * customers), then `onApplied({ code, freeDays, tierIds, message })` — or `onApplied(null)` when removed. The
 * Checkout re-checks it against the chosen plan; nothing is charged until the free period ends.
 */
export const PromoCodeField = ({ ccId, applied, onApplied, tone = 'light', disabled = false, align = 'start', onCheck }) => {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState(null)
  const t = TONES[tone] ?? TONES.light
  const centered = align === 'center'

  if (applied) {
    return (
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-[14px] ${t.applied}`}>
        <span className='flex items-center gap-2'>
          <Check className='h-4 w-4 text-[#16A34A]' aria-hidden='true' />
          <span>
            <span className='font-mono font-semibold'>{applied.code}</span> applied — {applied.message}
          </span>
        </span>
        <button type='button' onClick={() => onApplied(null)} disabled={disabled} className={`text-[13px] font-semibold ${t.link}`}>
          Remove
        </button>
      </div>
    )
  }

  if (!open) {
    return (
      <div className={centered ? 'text-center' : undefined}>
        <button type='button' onClick={() => setOpen(true)} disabled={disabled} className={`text-[13px] font-semibold ${t.link}`}>
          Have a promo code?
        </button>
      </div>
    )
  }

  const apply = async (event) => {
    event.preventDefault()
    if (!code.trim()) return setError('Enter a promo code.')
    setChecking(true)
    setError(null)
    try {
      // onCheck: the sign-up page checks without an account (no location yet); otherwise the location's own check.
      const result = onCheck ? await onCheck(code.trim()) : await checkPromoCode(ccId, code.trim())
      if (result?.valid) onApplied(result)
      else setError(result?.message || 'This promo code isn’t valid.')
    } catch (err) {
      setError(getFriendlyErrorMessage(err))
    } finally {
      setChecking(false)
    }
  }

  return (
    <form onSubmit={apply} noValidate className={`flex flex-col gap-1.5 ${centered ? 'items-center text-center' : ''}`}>
      <label htmlFor='promo-code-input' className={`text-[12px] font-semibold ${t.muted}`}>
        Promo code
      </label>
      <div className={`flex w-full gap-2 ${centered ? 'justify-center' : ''}`}>
        <input
          id='promo-code-input'
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete='off'
          autoFocus
          maxLength={40}
          className={`h-10 w-full max-w-[260px] rounded-lg border px-3 font-mono text-[14px] uppercase outline-none ${t.input}`}
          placeholder='Enter code'
        />
        <button
          type='submit'
          disabled={checking || disabled}
          className={`flex h-10 min-w-[84px] items-center justify-center rounded-lg border px-4 text-[13px] font-semibold disabled:opacity-60 ${t.button}`}
        >
          {checking ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Apply'}
        </button>
      </div>
      {error && (
        <p role='alert' className={`text-[13px] ${t.error}`}>
          {error}
        </p>
      )}
    </form>
  )
}
PromoCodeField.propTypes = {
  ccId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  applied: PropTypes.shape({ code: PropTypes.string, freeDays: PropTypes.number, tierIds: PropTypes.array, message: PropTypes.string }),
  onApplied: PropTypes.func.isRequired,
  tone: PropTypes.oneOf(['dark', 'light']),
  /** 'center' for the sign-up package page; 'start' (left) inside a card. */
  align: PropTypes.oneOf(['start', 'center']),
  /** `(code) => Promise<{ valid, code, freeDays, tierIds, message }>` — replaces the signed-in check (sign-up page). */
  onCheck: PropTypes.func,
  disabled: PropTypes.bool
}

/** Whether an applied promo covers this plan (no plan list = every plan; never the Free Trial). */
export const promoCovers = (promo, tier) =>
  Boolean(promo && tier && tier.kind !== 'TRIAL' && (!promo.tierIds?.length || promo.tierIds.includes(tier.id)))
